import { useState, FC } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import walletTabStyle from './WalletTab.module.scss';
import '../../../../index.scss';
import classNames from 'classnames';
import { ReactComponent as COPY } from '../../../../assets/images/icon_copy.svg';
import { ReactComponent as EXPLORE } from '../../../../assets/images/icon_external link.svg';
import { ReactComponent as DISCONNECT } from '../../../../assets/images/icon _exit.svg';
import { Asset } from './Asset/Asset';
import { BLOCKCHAINS, BLOCKCHAIN_EXPLORER } from '../../../../wallets/providers';
import { useTranslation } from 'react-i18next';
import { InfoContainer } from '../../InfoContainer/InfoContainer';
import { useAppDispatch, useAppSelector, useIsLoggedSelector } from '../../../../services/hooks.service';
import { useUltradeWallet } from '../../../../wallets/useUltradeWallet';
import { clearMainWallet } from '../../../../redux/_actions/user.action';
import { saveInitialState } from '../../../../redux/_actions/exchange.action';
import { useActiveAccount } from '../../../../wallets/useActiveAccount';
import { useMainWallet } from '../../../../wallets/useMainWallet';
import { ALGO_NETWORK } from '../../../../constants/constants';
import { SolanaNetwork } from '../../../../wallets/solana/network';

interface WalletTabProps {
  walletAddress: string;
  closeTab: (x: boolean) => void;
  hideAssets?: boolean;
}

export const WalletTab: FC<WalletTabProps> = ({ walletAddress, closeTab, hideAssets = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { disconnect } = useUltradeWallet();
  const activeWallet = useActiveAccount();
  const { isMainWallet, reconnect } = useMainWallet();
  const isLogged = useIsLoggedSelector();
  const sdkClient = useAppSelector((state) => state.sdkClient.instance);
  const rtkSdkClient = useAppSelector((state) => state.rtkSdkClient.instance);
  const mainAddress = useAppSelector((state) => state.user.mainAddress);
  const mainChain = useAppSelector((state) => state.user.mainChain);
  const assets = useAppSelector((state) => state.wallet.userAssets);
  const [copyWallet, setCopyWallet] = useState(false);

  const handleCopy = () => {
    setCopyWallet(true);
    setTimeout(() => setCopyWallet(false), 5000);
  };

  const handleDisconnect = async () => {
    const isTwoSolanaWallets = mainChain === BLOCKCHAINS.SOLANA && activeWallet?.chain === BLOCKCHAINS.SOLANA;
    if (!isMainWallet && activeWallet && !isTwoSolanaWallets) {
      reconnect();
    } else {
      await disconnect();
      sdkClient.mainWallet = null;
      rtkSdkClient.mainWallet = null;
      // solana proxy rpc update
      SolanaNetwork.updateConnection();
      dispatch(saveInitialState("wallet"));
      dispatch(clearMainWallet());
    }
    closeTab(false);
  }

  const getExplorerLink = () => {
    const wallet = activeWallet ?? {chain: mainChain, address: mainAddress};
    const query = (wallet?.chain === BLOCKCHAINS.SOLANA && ALGO_NETWORK !== 'MainNet') ? "?cluster=devnet" : "";
    return `${BLOCKCHAIN_EXPLORER[wallet?.chain]}/address/${wallet?.address}${query}`
  }

  return (
    <>
      { activeWallet &&
        <InfoContainer isWarning={!isMainWallet && isLogged} style={{margin: "8px 16px"}}>
          {!isLogged || (isLogged && isMainWallet) 
            ? t('connectWallet.main_wallet_text')
            : t('connectWallet.deposit_wallet_text')}
        </InfoContainer>
      }
      { !hideAssets && 
        <div className={walletTabStyle.wallet_body}>
          {assets.map((asset) => ( <div key={`${asset.index}${asset.unit_name}`} className={walletTabStyle.asset_row} >
            <Asset asset={asset} />
          </div>))}
        </div>
      }
      <div className={walletTabStyle.wallet_footer}>
        <CopyToClipboard text={walletAddress} onCopy={handleCopy}>
          <button className={walletTabStyle.footer_item} data-testid="copy">
            <COPY />
            {copyWallet ? t('comman_word.copied') : t('comman_word.copy')}
          </button>
        </CopyToClipboard>
        <a
          className={walletTabStyle.footer_item}
          href={getExplorerLink()}
          rel="noreferrer"
          target="_blank"
          data-testid="explore"
        >
          <EXPLORE style={{ marginTop: 2 }} />
          {t('comman_word.explore')}
        </a>
        <button data-testid="disconnect"
          className={classNames(walletTabStyle.footer_item)}
          onClick={handleDisconnect}
        >
          <DISCONNECT />
          {(activeWallet && activeWallet?.address !== mainAddress) 
            ? t('comman_word.opt_out')
            : t('comman_word.log_out') 
          }
        </button>
      </div>
    </>
  );
};
